import { HttpStatusCode } from "axios";
import clsx from "clsx";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useUserContext } from "../../../contexts/userContext";

import {
  FloorToiletInfo,
  getToiletMonitoringInfo,
  putToiletCleaning,
  ToiletInfo,
} from "../../../services/iotServices";
import { compartmentIsOpen, isCleaning } from "../../Services/ToiletEmpty";
import { logout } from "../../../services/authServices";

import useServiceGroupBuilding from "../../../hooks/useServiceGroupBuilding";
import useFullScreenLoading from "../../../hooks/useFullScreenLoading";
import useSimplePopup from "../../../hooks/useSimplePopup";

import StickyHeader from "../../../components/Layout/StickyHeader";
import CommonPopup from "../../../components/Popup";
import HorizontalRule from "../../../components/Rule/Horizontal";
import CommonButton from "../../../components/Button";
import { GoToTop } from "../../Talk";
import SimplePopup from "../../../components/Popup/Simple";

import { NoDataIcon } from "../../Center";

type Props = {};

export default function Toilet(props: Props) {
  const navigate = useNavigate();
  const [user] = useUserContext();
  const [floorToiletInfo, setFloorToiletInfo] = useState<FloorToiletInfo[]>([]);
  const scrollDivRef = React.useRef<HTMLDivElement>(null);
  const [selectedToiletInfo, setSelectedToiletInfo] = useState<ToiletInfo>();
  const [selectedBuilding, selectBuildingComponent] = useServiceGroupBuilding({
    categoryCode: "MONITORING",
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [popup, setPopup] = useState<string>("");
  const [simplePopupComponent, { open }] = useSimplePopup({});

  function isBatteryHidden(battery: number | undefined | null) {
    return battery === null || battery === undefined || battery === 0;
  }

  function calculateTimeDifference(pastTimeStr: string, utc: boolean = false) {
    const currentTime = new Date();
    const pastTime = new Date(pastTimeStr);

    let diffInMilliSeconds = Math.abs(
      currentTime.getTime() -
        (pastTime.getTime() + (utc ? 9 * 60 * 60 * 1000 : 0))
    );

    const hours = Math.floor(diffInMilliSeconds / (1000 * 60 * 60));
    diffInMilliSeconds -= hours * 1000 * 60 * 60;

    const minutes = Math.floor(diffInMilliSeconds / (1000 * 60));

    const stringArray = [];
    if (hours > 0) {
      stringArray.push(`${hours}시간`);
    }
    if (minutes > 0) {
      stringArray.push(`${minutes}분`);
    }

    return stringArray.join(" ");
  }

  const handleToiletClean = async (
    tagId: string | undefined,
    value: number | undefined | null
  ) => {
    if (tagId == null) {
      setPopup("cleaningInvalid");
      return;
    }
    try {
      setIsLoading(true);
      const res = await putToiletCleaning({
        tagId,
        cleaningYn: value !== 2,
      });
      if (res.status === HttpStatusCode.Ok) {
        if (value === 2) {
          setPopup("stopCleaning");
        } else {
          setPopup("startCleaning");
        }
      } else {
        setPopup("cleaningError");
      }
    } catch (e) {
    } finally {
      setIsLoading(false);
    }
  };

  const loadToiletInfo = async () => {
    setIsLoading(true);
    try {
      const res = await getToiletMonitoringInfo({
        buildingId: selectedBuilding?.buildingId,
      });
      if (res.status === HttpStatusCode.Ok) {
        setFloorToiletInfo(
          res.data[0].floors?.map((floors) => ({
            ...floors,
            toilets: floors.toilets.filter(
              (toilet) => toilet.compartments && toilet.compartments.length > 0
            ),
          }))
        );
      }
    } catch (e: any) {
      if (e?.response?.status === HttpStatusCode.Unauthorized) {
        logout();
      } else if (e?.response?.data?.message) {
        open(e.response.data.message?.replace(". ", ".\n"));
      } else {
        open("화장실 정보 획득 실패");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const batteryFormat = (battery: number | undefined) => {
    return battery ? `${battery}%` : "N/A";
  };

  useEffect(() => {
    if (user.isLoading === false && selectedBuilding?.buildingId) {
      loadToiletInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.isLoading, selectedBuilding?.buildingId]);

  return (
    <div className="px-5" ref={scrollDivRef}>
      {loadingComponent}
      <StickyHeader
        title="화장실 모니터링"
        onBackClick={() => navigate("/view")}
      ></StickyHeader>

      <div className="pb-7">{selectBuildingComponent}</div>
      <HorizontalRule margin="0px" />
      <div className="py-[30px]">
        <div className="py-5 text-lg font-medium">층 별 화장실 오염도</div>
        <div className=" bg-brand-weather-badge-background py-5 px-2.5 flex justify-center">
          배터리 부족 : 잔량이 10% 이하인 경우
        </div>
      </div>

      <HorizontalRule thickness={2} className="border-black" margin="0" />
      {floorToiletInfo?.filter((floor) => floor.toilets.length > 0)?.length &&
      floorToiletInfo?.filter((floor) => floor.toilets.length > 0)?.length >
        0 ? (
        floorToiletInfo
          .filter((floor) => floor.toilets.length > 0)
          .map((floorToiletInfo, index) => (
            <React.Fragment key={floorToiletInfo.floorName}>
              {index > 0 && (
                <HorizontalRule
                  thickness={1}
                  className=" border border-brand-neutral-30"
                  margin="0px"
                />
              )}
              <div key={floorToiletInfo.floorName} className="flex">
                <div className="py-5 text-lg font-medium w-[20%] flex items-center justify-center bg-brand-talk-search">
                  {floorToiletInfo.floorName}
                </div>
                <div className="flex flex-col w-full justify-center">
                  {floorToiletInfo.toilets.map((toiletInfo, index) => {
                    const clean = toiletInfo.clean[0];
                    const open = toiletInfo.open[0];
                    const batteryCount = toiletInfo.compartments.reduce(
                      (acc, current) => {
                        const tag = current.tag[0];
                        if (tag?.battery && tag.battery <= 10) {
                          return acc + 1;
                        } else {
                          return acc;
                        }
                      },
                      0
                    );
                    const totalOpenCount =
                      (open?.count ?? 0) +
                      (toiletInfo?.compartments.reduce((acc, cur) => {
                        return acc + (cur?.tag?.[0]?.count ?? 0);
                      }, 0) ?? 0);

                    return (
                      <React.Fragment key={toiletInfo.spaceToiletId}>
                        {index > 0 && <HorizontalRule margin="0px" />}
                        <div
                          className="flex justify-between px-4 py-6"
                          onClick={() => {
                            setSelectedToiletInfo(toiletInfo);
                            setPopup("toiletDetail");
                          }}
                        >
                          <div className="flex flex-col justify-center">
                            <div className="text-sm font-medium">
                              {toiletInfo.gender === "M"
                                ? "남자화장실"
                                : "여자화장실"}
                            </div>
                            {clean?.value && isCleaning(toiletInfo) ? (
                              <div className="text-xs text-white bg-brand-cleaning-badge rounded-full px-2 py-px w-fit">
                                청소중
                              </div>
                            ) : (
                              <></>
                            )}
                          </div>
                          <div className="flex items-center">
                            <div className="flex flex-col items-end">
                              <div className="text-sm font-semibold">
                                총 {totalOpenCount ?? 0}회 출입
                              </div>
                              {clean?.updatedAt ? (
                                <div className="text-xs text-brand-neutral-40 design_min:w-[86px] design_min:text-end">
                                  청소 후{" "}
                                  {calculateTimeDifference(
                                    clean?.updatedAt,
                                    false
                                  ) || "0분"}{" "}
                                  경과
                                </div>
                              ) : (
                                <div className="text-xs text-brand-neutral-40">
                                  이전 청소 기록이 없어요
                                </div>
                              )}
                              {batteryCount > 0 && (
                                <div className="text-xs text-red-600">
                                  센서 배터리 부족 : 칸 {batteryCount}개
                                </div>
                              )}
                            </div>
                            <div>
                              <ToiletListRightIcon />
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </React.Fragment>
          ))
      ) : (
        <div className="sticky top-[72px] w-full h-40 flex flex-col items-center justify-center gap-2">
          <NoDataIcon />
          센서 데이터가 없어요
        </div>
      )}
      <HorizontalRule thickness={2} className="border-black" margin="0" />

      <CommonPopup
        title="화장실 오염도 현황"
        isOpen={selectedToiletInfo !== undefined && popup === "toiletDetail"}
        padding={0}
        onClose={() => {
          setSelectedToiletInfo(undefined);
          setPopup("");
        }}
        unUseHorizonRule
      >
        <div className="flex flex-col items-center px-5 gap-2.5">
          <div className="flex gap-1 p-1.5">
            <div
              className={clsx([
                { "opacity-60": selectedToiletInfo?.gender === "F" },
              ])}
            >
              <ToiletPopupManIcon />
            </div>
            <div
              className={clsx([
                { "opacity-60": selectedToiletInfo?.gender === "M" },
              ])}
            >
              <ToiletPopupWomanIcon />
            </div>
          </div>
          <div className="text-3xl font-semibold pb-[15px]">
            {selectedToiletInfo?.toiletName}
          </div>
          <HorizontalRule
            margin="0"
            className="border-[#dfdfdf] border-t-[1px] w-full"
          />
          <div className="grid grid-cols-3 w-full pt-2.5 pb-5 px-[30px]">
            <div className="flex flex-col items-center">
              <div className="text-xs text-brand-neutral-60">출입</div>
              <div className="text-xl font-semibold">
                {selectedToiletInfo?.open?.[0]?.count ?? 0}회
              </div>
              <div
                className={clsx([
                  "text-sm text-brand-neutral-60",
                  {
                    hidden: isBatteryHidden(
                      selectedToiletInfo?.open?.[0]?.battery
                    ),
                  },
                ])}
              >
                배터리{" "}
                <span
                  className={clsx([
                    "font-medium",
                    {
                      "text-red-600":
                        (selectedToiletInfo?.open?.[0]?.battery ?? 0) < 10,
                    },
                  ])}
                >
                  {batteryFormat(selectedToiletInfo?.open?.[0]?.battery)}
                </span>
              </div>
            </div>
            <div className="flex flex-col items-center">
              <div className="text-xs text-brand-neutral-60">칸 전체</div>
              <div className="text-xl font-semibold">
                {selectedToiletInfo?.compartments.reduce((acc, cur) => {
                  return acc + (cur?.tag?.[0]?.count ?? 0);
                }, 0)}
                회
              </div>
            </div>
            <div className="flex flex-col items-center">
              <div className="text-xs text-brand-neutral-60">미화버튼</div>

              <div className="text-xl font-semibold">
                {isCleaning(selectedToiletInfo) ? "청소중" : "청소완료"}
              </div>

              <div
                className={clsx([
                  "text-sm text-brand-neutral-60",
                  {
                    hidden: isBatteryHidden(
                      selectedToiletInfo?.clean?.[0]?.battery
                    ),
                  },
                ])}
              >
                배터리{" "}
                <span
                  className={clsx([
                    "font-medium",
                    {
                      "text-red-600":
                        (selectedToiletInfo?.clean?.[0]?.battery ?? 0) < 10,
                    },
                  ])}
                >
                  {batteryFormat(selectedToiletInfo?.clean?.[0]?.battery)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-brand-talk-search flex justify-between px-5 py-4 text-sm text-brand-neutral-90 items-center">
          {selectedToiletInfo?.clean?.[0]?.updatedAt ? (
            <div>
              마지막 청소 후{" "}
              <span className="font-semibold">
                {calculateTimeDifference(
                  selectedToiletInfo?.clean?.[0]?.updatedAt,
                  false
                )}
              </span>{" "}
              경과 되었어요
            </div>
          ) : (
            <div>이전 청소 기록이 없어요</div>
          )}
          <div
            className="text-sm font-bold text-white bg-brand-cleaning-badge rounded-full px-4 py-1.5 whitespace-nowrap"
            onClick={() => {
              handleToiletClean(
                selectedToiletInfo?.clean?.[0]?.tagId,
                selectedToiletInfo?.clean?.[0]?.value
              );
            }}
          >
            {selectedToiletInfo?.clean?.[0]?.value === 2
              ? "청소 완료"
              : "청소 시작"}
          </div>
        </div>

        <div className="px-5 grid grid-cols-5 pt-10 pb-20">
          {selectedToiletInfo?.compartments.map((compartment, index) => (
            <div className="flex flex-col items-center gap-1" key={index}>
              {!compartmentIsOpen(compartment) ? (
                <ToiletPopupDoorCloseIcon />
              ) : (
                <ToiletPopupDoorOpenIcon />
              )}
              <div className="flex flex-col items-center">
                <div className="text-black text-base">
                  <span
                    className={clsx([
                      "font-medium",
                      {
                        "text-red-600": (compartment.tag?.[0]?.count ?? 0) > 20,
                      },
                    ])}
                  >
                    {compartment.tag?.[0]?.count ?? 0}
                  </span>
                  회
                </div>
                <div
                  className={clsx([
                    "text-sm text-brand-neutral-60",

                    {
                      hidden: isBatteryHidden(compartment.tag?.[0]?.battery),
                      "text-red-600": (compartment.tag?.[0]?.battery ?? 0) < 10,
                    },
                  ])}
                >
                  {batteryFormat(compartment.tag?.[0]?.battery)}
                </div>
              </div>
            </div>
          ))}
        </div>

        <CommonButton
          className="w-full rounded-none h-bottom-button"
          onClick={() => {
            setSelectedToiletInfo(undefined);
            setPopup("");
          }}
        >
          확인
        </CommonButton>
      </CommonPopup>

      <SimplePopup
        isOpen={popup === "startCleaning"}
        onClose={async () => {
          setPopup("");
          setTimeout(async () => {
            await loadToiletInfo();
          }, 1100);
        }}
        message="청소 시작 처리되었습니다."
      />

      <SimplePopup
        isOpen={popup === "stopCleaning"}
        onClose={async () => {
          setPopup("");
          setTimeout(async () => {
            await loadToiletInfo();
          }, 1100);
        }}
        message="청소 완료 처리되었습니다."
      />

      <SimplePopup
        isOpen={popup === "cleaningError"}
        onClose={() => setPopup("")}
        message="에러가 발생했습니다"
      />

      <SimplePopup
        isOpen={popup === "cleaningInvalid"}
        onClose={() => setPopup("")}
        message="청소를 시작할 수 없는 상태입니다."
      />

      <div className="fixed bottom-5 right-5 flex flex-col gap-2.5 z-20">
        <div
          className="border border-brand-floating-secondary-button rounded-[10px] p-2 bg-white"
          onClick={() => {
            scrollDivRef.current?.scrollIntoView({ behavior: "smooth" });
          }}
          style={{ boxShadow: "2px 5px 4px 0px rgba(0, 0, 0, 0.10)" }}
        >
          <GoToTop />
        </div>
      </div>
      {simplePopupComponent}
    </div>
  );
}

export const ToiletListRightIcon = () => (
  <svg
    width="24"
    height="25"
    viewBox="0 0 24 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g opacity="0.5">
      <path
        d="M9 18.9954L15 12.9954L9 6.99536"
        stroke="black"
        strokeWidth="1.75"
        strokeLinecap="square"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);

export const ToiletPopupManIcon = () => (
  <svg
    width="10"
    height="27"
    viewBox="0 0 10 27"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8.10597 3.45826C8.10597 5.32423 6.59348 6.83693 4.72717 6.83693C2.86086 6.83693 1.34863 5.32423 1.34863 3.45826C1.34863 1.59229 2.86112 0.0795898 4.72717 0.0795898C6.59321 0.0795898 8.10597 1.59229 8.10597 3.45826Z"
      fill="url(#paint0_linear_1706_223)"
    />
    <path
      d="M8.63646 10.5543L8.63643 10.5545L4.76857 25.199C4.76855 25.1991 4.76854 25.1992 4.76853 25.1992C4.76173 25.2248 4.75411 25.2379 4.75064 25.2431C4.7471 25.2484 4.7448 25.2502 4.7442 25.2506C4.74267 25.2518 4.73719 25.2549 4.72709 25.2549C4.71698 25.2549 4.71154 25.2517 4.7101 25.2507C4.70955 25.2503 4.70731 25.2486 4.70383 25.2434C4.70042 25.2383 4.69281 25.2252 4.68605 25.1996L4.68596 25.1992L0.81782 10.5547C0.817816 10.5547 0.817812 10.5547 0.817807 10.5547C0.806504 10.5118 0.816663 10.475 0.834354 10.4504C0.842939 10.4384 0.850658 10.4332 0.853652 10.4316L0.853718 10.4316C0.855284 10.4307 0.855863 10.4304 0.859182 10.4304H8.595C8.59836 10.4304 8.59896 10.4307 8.60061 10.4316L8.60063 10.4316C8.60368 10.4333 8.61142 10.4385 8.62 10.4504C8.63767 10.475 8.64775 10.5116 8.63646 10.5543Z"
      stroke="black"
      strokeWidth="1.5"
    />
    <defs>
      <linearGradient
        id="paint0_linear_1706_223"
        x1="8.10597"
        y1="0.0795898"
        x2="0.911691"
        y2="1.93121"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E0004D" />
        <stop offset="1" stopColor="#EA27C2" />
      </linearGradient>
    </defs>
  </svg>
);

export const ToiletPopupWomanIcon = () => (
  <svg
    width="9"
    height="27"
    viewBox="0 0 9 27"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M5.94091 18.4316L8.15174 25.2554H0.827313L3.03814 18.4316C3.03814 18.4316 3.03815 18.4316 3.03816 18.4315C3.13085 18.1455 3.13085 17.8372 3.03816 17.5511C3.03815 17.5511 3.03814 17.5511 3.03814 17.5511L0.827313 10.7273H8.15174L5.94091 17.5511C5.94091 17.5511 5.9409 17.5511 5.9409 17.5511C5.8482 17.8372 5.8482 18.1455 5.9409 18.4315C5.9409 18.4316 5.94091 18.4316 5.94091 18.4316Z"
      stroke="black"
      strokeWidth="1.5"
    />
    <path
      d="M4.48949 6.86325C6.38324 6.86325 7.91843 5.32806 7.91843 3.43431C7.91843 1.54056 6.38324 0.00537109 4.48949 0.00537109C2.59573 0.00537109 1.06055 1.54056 1.06055 3.43431C1.06055 5.32806 2.59573 6.86325 4.48949 6.86325Z"
      fill="url(#paint0_linear_1706_1169)"
    />
    <defs>
      <linearGradient
        id="paint0_linear_1706_1169"
        x1="7.91843"
        y1="0.00537109"
        x2="0.617104"
        y2="1.88454"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E0004D" />
        <stop offset="1" stopColor="#EA27C2" />
      </linearGradient>
    </defs>
  </svg>
);

export const ToiletPopupDoorCloseIcon = () => (
  <svg
    width="52"
    height="52"
    viewBox="0 0 52 52"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g opacity="0.3">
      <rect
        x="11"
        y="5"
        width="30"
        height="42"
        stroke="black"
        strokeWidth="2"
      />
      <rect
        x="33"
        y="20"
        width="4"
        height="12"
        rx="2"
        stroke="black"
        strokeWidth="2"
      />
    </g>
  </svg>
);

export const ToiletPopupDoorOpenIcon = () => (
  <svg
    width="52"
    height="52"
    viewBox="0 0 52 52"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="11"
      y="5"
      width="30"
      height="42"
      fill="url(#paint0_linear_940_18393)"
      stroke="black"
      strokeWidth="2"
    />
    <path
      d="M11 47.1271V4.87289L31 2.14562V49.8544L11 47.1271Z"
      fill="white"
      stroke="black"
      strokeWidth="2"
    />
    <rect
      x="23"
      y="20"
      width="4"
      height="12"
      rx="2"
      stroke="black"
      strokeWidth="2"
    />
    <defs>
      <linearGradient
        id="paint0_linear_940_18393"
        x1="26"
        y1="4"
        x2="26"
        y2="48"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E7004B" />
        <stop offset="1" stopColor="#ED27B8" />
      </linearGradient>
    </defs>
  </svg>
);
